import React, { Fragment, useEffect, useRef, useState } from 'react';
import { TiArrowSortedDown } from 'react-icons/ti';
import PlusIcon from '../../assets/icons/add.svg';
import { FilterDropdown } from './FilterDropdown';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import ChevronDown from '../../assets/icons/chevron-down.svg';
import { Label } from '../ui/label';

const toQaSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'item';

const isAllItemsOption = (value: string) => value.trim().toLowerCase() === 'all items';

const buildFilterQaPrefix = (dataQaPrefix: string | undefined, filterKey: string) => {
  const safeKey = filterKey || 'filter';
  return dataQaPrefix ? `${dataQaPrefix}-filter-${safeKey}` : `filter-${safeKey}`;
};

const buildItemQa = (qaPrefix: string, itemType: 'index' | 'name', name: string, index: number) => {
  if (itemType === 'name' && name) {
    return `${qaPrefix}-dropdownitem-${toQaSlug(name)}`;
  }
  return `${qaPrefix}-dropdownitem-${index}`;
};

const getFilterItemValue = (item: any): string =>
  String(item?.value ?? item?.displayName ?? item?.name ?? '').trim();

const getFilterItemLabel = (item: any): string =>
  String(item?.label ?? item?.displayName ?? item?.name ?? item?.value ?? '').trim();

const collectFilterItemLabels = (items: any[]): Map<string, string> => {
  const labelsByValue = new Map<string, string>();

  const visit = (entry: any) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const value = getFilterItemValue(entry);
    const label = getFilterItemLabel(entry);
    if (value && label && !labelsByValue.has(value)) {
      labelsByValue.set(value, label);
    }
    if (Array.isArray(entry.subClients)) {
      entry.subClients.forEach(visit);
    }
  };

  if (Array.isArray(items)) {
    items.forEach(visit);
  }

  return labelsByValue;
};

const Filter = (props: any) => {
  const {
    shadcn,
    handleClearAllFilter,
    handleChooseFilter,
    handleShowArchived,
    filter_name,
    filter_values,
    filter_key,
    items,
    multiple_select,
    archived,
    show_option_all,
    archivedCheckbox,
    archivedProjectsFunction,
    max_depth,
    new_status_option,
    handleChangeNewStatusFilter,
    handleSetNewStatusFilter,
    handleSaveNewStatus,
    newStatusFilterValue,
    newStatusFilter,
    newStatusRef,
    handleResetNewStatus,
    isMobile = false,
    dataQaPrefix,
    dataQaItemType,
  } = props;

  const filter_ref = useRef<HTMLDivElement>(null);
  const filter_dropdown_ref = useRef<HTMLDivElement>(null);

  const [active, setActive] = useState(false);
  const qaPrefix = buildFilterQaPrefix(dataQaPrefix, filter_key);
  const itemType: 'index' | 'name' = dataQaItemType || 'index';
  const allowNewStatus = new_status_option && filter_key !== 'status';
  const labelsByValue = collectFilterItemLabels(items || []);
  let itemIndex = 0;
  const nextIndex = () => {
    itemIndex += 1;
    return itemIndex;
  };

  const handleFilterSelected = () => {
    let name = 'All';

    if (filter_values && filter_values.length === 1) {
      const selectedValue = String(filter_values[0] || '');
      const resolvedLabel = labelsByValue.get(selectedValue) || selectedValue;
      name = resolvedLabel;
      if (resolvedLabel.length > 20) {
        name = resolvedLabel.slice(0, 20) + '...';
      }
    } else if (filter_values && filter_values.length > 1) {
      name = filter_values.length + ' ' + 'selected';
    }

    return name;
  };

  const handleOnChooseFilter = (val: string, key: string) => {
    handleChooseFilter(val, key);
    if (!multiple_select) setActive(false);
  };

  // Click-away listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filter_ref.current &&
        !filter_ref.current.contains(event.target as Node) &&
        filter_dropdown_ref.current &&
        !filter_dropdown_ref.current.contains(event.target as Node)
      ) {
        setActive(false);
        if (handleResetNewStatus) handleResetNewStatus();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filter_ref]);

  // Temp prop for only certain filters to have new style (shadcn)
  if (shadcn) {
    return (
      <Popover open={active} onOpenChange={setActive} modal={false}>
        <PopoverTrigger data-qa={`projects_${filter_key}_filter_trigger`} asChild>
          <Button
            className={`${isMobile ? 'w-full' : ''} ${filter_values?.length > 0 ? 'border-primary border-2 text-primary bg-primary/5' : ''}`}
            variant={'filter'}
            data-qa={`${qaPrefix}-toggle`}
          >
            <span className="truncate">
              {isMobile
                ? handleFilterSelected()
                : `${filter_name || 'Filter'}: ${handleFilterSelected()}`}
            </span>
            <div className={`icon transition ${active ? 'rotate-180' : ''}`}>
              <ChevronDown />
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align={'start'}
          className={`${isMobile ? '' : 'w-[220px]'} p-[4px]`}
          data-qa={`${qaPrefix}-menu`}
        >
          <div className="max-h-[300px] overflow-y-auto overflow-x-hidden">
            {allowNewStatus && (
              <div
                data-qa="projects_status_filter_create_new_status_trigger"
                onClick={() => {
                  if (handleSetNewStatusFilter) handleSetNewStatusFilter();
                }}
                className="dropdown_body_row new_status"
              >
                <div>
                  <PlusIcon className="plus_icon" />
                </div>

                <div>
                  <span>New Status</span>
                </div>
              </div>
            )}

            {allowNewStatus && newStatusFilter && (
              <div
                className="dropdown_body_row flex items-center justify-between cursor-auto"
                style={{ gap: '15px' }}
              >
                <div className="new_status_input_container">
                  <input
                    data-qa="projects_status_filter_create_status_input"
                    ref={newStatusRef || null}
                    value={newStatusFilterValue || ''}
                    onChange={(e) => {
                      if (handleChangeNewStatusFilter) handleChangeNewStatusFilter(e);
                    }}
                    placeholder="Enter Name..."
                    className="input"
                  />
                </div>

                {newStatusFilterValue && (
                  <div className="new_status_save_btn_container">
                    <button
                      data-qa="projects_status_filter_create_status_trigger"
                      type="button"
                      onClick={() => {
                        if (handleSaveNewStatus) handleSaveNewStatus();
                      }}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            )}

            {show_option_all && (
              <div
                data-qa={`projects_${filter_key}_filter_all_option`}
                className={`px-[8px] py-[12px] rounded-sm cursor-pointer hover:bg-muted text-sm ${filter_values?.length < 1 ? 'bg-primary/10 text-primary font-medium' : ''}`}
                onClick={() => handleOnChooseFilter('', filter_key)}
              >
                <span>All</span>
              </div>
            )}

            {items &&
              items.length > 0 &&
              items.map((i: any, index: any) => {
                const itemValue = getFilterItemValue(i);
                const itemLabel = getFilterItemLabel(i) || itemValue;
                if (multiple_select) {
                  const isAllItems = isAllItemsOption(itemLabel);
                  const isChecked = isAllItems
                    ? filter_values.length < 1 ||
                      (filter_values.length === 1 && isAllItemsOption(filter_values[0]))
                    : filter_values.includes(itemValue);
                  return (
                    <div
                      key={index}
                      data-qa={`projects_${filter_key}_checkbox_option_${itemLabel}`}
                      className="flex items-center gap-2 px-[12px] py-[8px] rounded-sm cursor-pointer hover:bg-muted text-sm break-words"
                      onClick={() => handleOnChooseFilter(itemValue, filter_key)}
                    >
                      <Checkbox
                        className="peer h-[16px] w-[16px] shrink-0 border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                        checked={isChecked}
                      />

                      <span className="text-sm cursor-pointer flex-1 break-all">{itemLabel}</span>
                    </div>
                  );
                }
                return (
                  <Fragment key={index}>
                    <div
                      data-qa={`projects_${filter_key}_filter_option_${itemLabel}`}
                      className={`px-[12px] py-[8px] rounded-sm cursor-pointer hover:bg-muted text-sm break-words ${filter_values?.[0] === itemValue ? 'bg-primary/10 text-primary font-medium' : ''}`}
                      onClick={() => handleOnChooseFilter(itemValue, filter_key)}
                    >
                      <span>{itemLabel}</span>
                    </div>

                    {i.subClients &&
                      i.subClients.length > 0 &&
                      i.subClients.map((item: any, idx: any) => {
                        return recursiveClient(
                          item,
                          filter_key,
                          0,
                          handleOnChooseFilter,
                          max_depth,
                          filter_values,
                          nextIndex,
                          qaPrefix,
                          itemType
                        );
                      })}
                  </Fragment>
                );
              })}
          </div>

          <div>
            {archived ? (
              <Label
                htmlFor={`checkbox_archive_${filter_key}`}
                className="border-t border-input font-normal flex items-center gap-2 px-[12px] py-[8px] rounded-sm cursor-pointer hover:bg-muted text-sm break-words"
                data-qa={`projects_${filter_key}_show_archived_option`}
              >
                <Checkbox
                  id={`checkbox_archive_${filter_key}`}
                  onCheckedChange={() =>
                    handleShowArchived(archivedProjectsFunction ? !archivedCheckbox : filter_key)
                  }
                  className="peer h-[16px] w-[16px] shrink-0 border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                  checked={archivedCheckbox}
                />
                {/* <input
                  className="checkbox not_interactive"
                  type="checkbox"
                  checked={archivedCheckbox}
                /> */}
                <span>Show archived</span>
              </Label>
            ) : null}

            {multiple_select && (
              <div>
                <div className="w-full">
                  {items && items.length < 1 ? (
                    <div className="px-[12px] py-[8px] rounded-sm text-sm break-words">
                      <span>No Items</span>
                    </div>
                  ) : filter_values?.length > 0 ? (
                    <div className="border-t border-input mt-[5px]">
                      <Button
                        data-qa={`projects_${filter_key}_clear_all_option`}
                        className="mt-[5px]"
                        onClick={() => handleClearAllFilter(filter_key)}
                        variant={'clearFilter'}
                        type="button"
                      >
                        Clear All
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div ref={filter_ref} className={`${filter_key}_filter`} data-qa={qaPrefix}>
      <div
        onClick={() => setActive((prev) => !prev)}
        className="title"
        data-qa={`${qaPrefix}-toggle`}
      >
        <span>
          {filter_name || 'Filter'}: <span className="font-medium">{handleFilterSelected()}</span>
        </span>
        <div className={`icon ${active ? 'rotate' : ''}`}>
          <TiArrowSortedDown style={{ fontSize: '18px' }} />
        </div>
      </div>

      <FilterDropdown
        isActive={active}
        buttonRef={filter_ref}
        dropdownRef={filter_dropdown_ref}
        filter_key={filter_key}
      >
        {allowNewStatus && (
          <div
            onClick={() => {
              if (handleSetNewStatusFilter) handleSetNewStatusFilter();
            }}
            className="dropdown_body_row new_status"
            data-qa={`${qaPrefix}-dropdownitem-new-status`}
          >
            <div>
              <PlusIcon className="plus_icon" />
            </div>

            <div>
              <span>New Status</span>
            </div>
          </div>
        )}

        {allowNewStatus && newStatusFilter && (
          <div
            className="dropdown_body_row flex items-center justify-between cursor-auto"
            style={{ gap: '15px' }}
          >
            <div className="new_status_input_container">
              <input
                ref={newStatusRef || null}
                value={newStatusFilterValue || ''}
                onChange={(e) => {
                  if (handleChangeNewStatusFilter) handleChangeNewStatusFilter(e);
                }}
                placeholder="Enter Name..."
                className="input"
                data-qa={`${qaPrefix}-new-status-input`}
              />
            </div>

            {newStatusFilterValue && (
              <div className="new_status_save_btn_container">
                <button
                  type="button"
                  onClick={() => {
                    if (handleSaveNewStatus) handleSaveNewStatus();
                  }}
                  data-qa={`${qaPrefix}-new-status-save`}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        )}

        <div className="dropdown_body">
          {show_option_all && (
            <div
              className="dropdown_body_row"
              onClick={() => handleOnChooseFilter('', filter_key)}
              data-qa={`${qaPrefix}-dropdownitem-all`}
            >
              <span>All</span>
            </div>
          )}
          {items &&
            items.length > 0 &&
            items.map((i: any, index: any) => {
              const itemValue = getFilterItemValue(i);
              const itemLabel = getFilterItemLabel(i) || itemValue;
              const itemQa = buildItemQa(qaPrefix, itemType, itemLabel, nextIndex());
              if (multiple_select) {
                const isAllItems = isAllItemsOption(itemLabel);
                const isChecked = isAllItems
                  ? filter_values.length < 1 ||
                    (filter_values.length === 1 && isAllItemsOption(filter_values[0]))
                  : filter_values.includes(itemValue);
                return (
                  <div
                    className="dropdown_body_row"
                    onClick={() => handleOnChooseFilter(itemValue, filter_key)}
                    data-qa={itemQa}
                  >
                    {multiple_select && (
                      <div className="checkbox_container">
                        <input
                          checked={isChecked}
                          className="checkbox not_interactive"
                          type="checkbox"
                        />
                      </div>
                    )}

                    <span className="filter_value">{itemLabel}</span>
                  </div>
                );
              }
              return (
                <Fragment key={index}>
                  <div
                    className="dropdown_body_row"
                    onClick={() => handleOnChooseFilter(itemValue, filter_key)}
                    data-qa={itemQa}
                  >
                    <span>{itemLabel}</span>
                  </div>

                  {i.subClients &&
                    i.subClients.length > 0 &&
                    i.subClients.map((item: any, idx: any) => {
                      return recursiveClient(
                        item,
                        filter_key,
                        0,
                        handleOnChooseFilter,
                        max_depth,
                        undefined,
                        nextIndex,
                        qaPrefix,
                        itemType
                      );
                    })}
                </Fragment>
              );
            })}
        </div>

        {archived ? (
          <Label
            htmlFor={`checkbox_archive_${filter_key}`}
            className="border-t border-input font-normal flex items-center gap-2 px-[12px] py-[8px] rounded-sm cursor-pointer hover:bg-muted text-sm break-words"
            data-qa={`${qaPrefix}-dropdownitem-show-archived`}
          >
            <Checkbox
              id={`checkbox_archive_${filter_key}`}
              onCheckedChange={() =>
                handleShowArchived(archivedProjectsFunction ? !archivedCheckbox : filter_key)
              }
              className="peer h-[16px] w-[16px] shrink-0 border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
              checked={archivedCheckbox}
            />
            {/* <input
              className="checkbox not_interactive"
              type="checkbox"
              checked={archivedCheckbox}
            /> */}
            <span>Show archived</span>
          </Label>
        ) : null}

        {multiple_select && (
          <div className="dropdown_body_row" data-qa={`${qaPrefix}-dropdownitem-clear-all`}>
            <div className="w-full">
              {items && items.length < 1 ? (
                <span>No Items</span>
              ) : (
                <button
                  onClick={() => handleClearAllFilter(filter_key)}
                  className="clear_filter_btn text-left"
                  type="button"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        )}
      </FilterDropdown>
    </div>
  );
};

const recursiveClient = (
  client: any,
  filter_key: any,
  level: any = 0,
  handleOnChooseFilter: any,
  max_depth?: any,
  filter_values?: any,
  getNextIndex?: () => number,
  qaPrefix?: string,
  itemType: 'index' | 'name' = 'index'
) => {
  const val =
    (client && client.displayName) || (client && client.name) || (client && client.value) || '';
  const clientId = client && client.id;

  if (!client.active) return null;

  const padding_left = 28 + level * 16 + 'px';

  if (max_depth !== undefined && level >= max_depth) return;

  return (
    <Fragment key={clientId || val}>
      <div
        data-qa={`projects_${filter_key}_filter_option_${val}`}
        className={`py-[8px] rounded-sm cursor-pointer hover:bg-muted text-sm break-words ${filter_values?.[0] === val ? 'bg-primary/10 text-primary font-medium' : ''}`}
        style={{ paddingLeft: padding_left, paddingRight: '12px' }}
        onClick={() => (handleOnChooseFilter ? handleOnChooseFilter(val, filter_key) : {})}
      >
        <span>{val}</span>
      </div>

      {client.subClients &&
        client.subClients.length > 0 &&
        client.subClients.map((item: any) => {
          return recursiveClient(
            item,
            filter_key,
            level + 1,
            handleOnChooseFilter,
            max_depth,
            filter_values,
            getNextIndex,
            qaPrefix,
            itemType
          );
        })}
    </Fragment>
  );
};

export default Filter;
